import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getDatabase, ref, set, onValue } from 'firebase/database';

function VacationMode() {
  const [openDialog, setOpenDialog] = useState(false);
  const [vacationDates, setVacationDates] = useState({
    startDate: null,
    endDate: null
  });
  const [currentVacation, setCurrentVacation] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const db = getDatabase();
    const vacationRef = ref(db, 'vacation');
    
    const unsubscribe = onValue(vacationRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        try {
          const startDate = new Date(data.startDate);
          const endDate = new Date(data.endDate);
          if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            setCurrentVacation({
              startDate,
              endDate
            });
          }
        } catch (error) {
          console.error('Error parsing vacation dates:', error);
        }
      } else {
        setCurrentVacation(null);
      }
    }, (error) => {
      console.error('Error reading vacation data:', error);
      setSnackbar({
        open: true,
        message: 'Error reading vacation data: ' + error.message,
        severity: 'error'
      });
    });

    return () => unsubscribe();
  }, []);

  const handleSetVacation = async () => {
    try {
      const { startDate, endDate } = vacationDates;
      
      if (!startDate || !endDate) {
        setSnackbar({
          open: true,
          message: 'Please select both start and end dates',
          severity: 'error'
        });
        return;
      }

      if (startDate >= endDate) {
        setSnackbar({
          open: true,
          message: 'End date must be after start date',
          severity: 'error'
        });
        return;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (startDate < today) {
        setSnackbar({
          open: true,
          message: 'Start date cannot be in the past',
          severity: 'error'
        });
        return;
      }

      const db = getDatabase();
      await set(ref(db, 'vacation'), {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        updatedAt: new Date().toISOString(),
        active: true
      });

      setSnackbar({
        open: true,
        message: 'Vacation mode set successfully',
        severity: 'success'
      });
      setOpenDialog(false);
      setVacationDates({ startDate: null, endDate: null });
    } catch (error) {
      console.error('Error setting vacation mode:', error);
      setSnackbar({
        open: true,
        message: 'Error setting vacation mode: ' + error.message,
        severity: 'error'
      });
    }
  };

  const handleClearVacation = async () => {
    try {
      const db = getDatabase();
      await set(ref(db, 'vacation'), null);

      setSnackbar({
        open: true,
        message: 'Vacation mode cleared successfully',
        severity: 'success'
      });
      setCurrentVacation(null);
    } catch (error) {
      console.error('Error clearing vacation mode:', error);
      setSnackbar({
        open: true,
        message: 'Error clearing vacation mode: ' + error.message,
        severity: 'error'
      });
    }
  };

  // Only show if we're in the admin page
  if (!sessionStorage.getItem('adminAuthenticated')) {
    return null;
  }

  return (
    <Box sx={{ mt: 2, mb: 4 }}>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setOpenDialog(true)}
        sx={{ mr: 2 }}
      >
        Set Vacation Mode
      </Button>
      {currentVacation && (
        <>
          <Alert severity="info" sx={{ mt: 2 }}>
            Current vacation period: {currentVacation.startDate.toLocaleDateString()} to{' '}
            {currentVacation.endDate.toLocaleDateString()}
          </Alert>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClearVacation}
            sx={{ mt: 2 }}
          >
            Clear Vacation Period
          </Button>
        </>
      )}

      <Dialog 
        open={openDialog} 
        onClose={() => {
          setOpenDialog(false);
          setVacationDates({ startDate: null, endDate: null });
        }}
      >
        <DialogTitle>Set Vacation Mode</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Set your vacation period to let customers know when you'll be unavailable.
          </DialogContentText>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <DatePicker
                label="Start Date"
                value={vacationDates.startDate}
                onChange={(newValue) =>
                  setVacationDates((prev) => ({ ...prev, startDate: newValue }))
                }
                minDate={new Date()}
                slotProps={{
                  textField: {
                    helperText: 'Select start date'
                  }
                }}
              />
              <DatePicker
                label="End Date"
                value={vacationDates.endDate}
                onChange={(newValue) =>
                  setVacationDates((prev) => ({ ...prev, endDate: newValue }))
                }
                minDate={vacationDates.startDate || new Date()}
                slotProps={{
                  textField: {
                    helperText: 'Select end date'
                  }
                }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setOpenDialog(false);
              setVacationDates({ startDate: null, endDate: null });
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSetVacation} variant="contained">
            Set Vacation
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default VacationMode;
